/**
 * The meditations the therapist gave this patient.
 *
 * A list only; each one opens on its own page, because a meditation is used
 * one at a time and with the eyes closed. Nothing here can change a meditation
 * — the portal has no write path to it at all, which is what makes it safe to
 * show work the therapist keeps and edits in their own library.
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { AudioLines, FileText } from "lucide-react";

import MeditationIcon from "../../../../shared/ui/icons/MeditationIcon";
import { listPortalMeditations } from "../../application/portalServices";
import type { PortalMeditation } from "../../domain/types";
import { formatDuration } from "../../../meditation";

export default function PortalMeditationsPage() {
    const { t, i18n } = useTranslation();
    const [meditations, setMeditations] = useState<PortalMeditation[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        let cancelled = false;

        listPortalMeditations()
            .then((data) => {
                if (cancelled) return;
                setMeditations(data);
                setLoading(false);
            })
            .catch(() => {
                if (cancelled) return;
                setError(true);
                setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, []);

    const givenOn = (iso: string) =>
        new Date(iso).toLocaleDateString(i18n.language, {
            day: "numeric",
            month: "long",
            year: "numeric",
        });

    /* What the author says it takes, when they said; the script's own
       arithmetic otherwise. */
    const runsFor = (meditation: PortalMeditation) =>
        formatDuration(
            meditation.declared_duration_seconds ??
                meditation.audio_duration_seconds ??
                meditation.estimated_seconds
        );

    return (
        <>
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-content-primary">
                    {t("portal.meditations.title")}
                </h1>
                <p className="mt-1 text-sm text-content-tertiary">
                    {t("portal.meditations.subtitle")}
                </p>
            </div>

            {loading && <p className="text-sm text-gray-500">{t("common.loading")}</p>}

            {!loading && error && (
                <p role="alert" className="text-sm text-red-600">
                    {t("portal.meditations.loadError")}
                </p>
            )}

            {!loading && !error && meditations.length === 0 && (
                <div className="glass-effect glass-border rounded-2xl border p-8 text-center">
                    <MeditationIcon
                        size={24}
                        className="mx-auto mb-3 text-content-tertiary"
                    />
                    <p className="text-sm text-content-secondary">
                        {t("portal.meditations.empty")}
                    </p>
                </div>
            )}

            {!loading && !error && meditations.length > 0 && (
                <ul className="grid gap-4 sm:grid-cols-2">
                    {meditations.map((meditation) => (
                        <li key={meditation.id}>
                            <Link
                                to={`/portal/meditations/${meditation.id}`}
                                className="glass-effect glass-border flex h-full flex-col gap-2 rounded-2xl border p-5 shadow-sm transition-colors duration-fast hover:bg-surface-sunken focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                            >
                                <span className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-content-tertiary">
                                    {meditation.has_audio ? (
                                        <AudioLines size={14} aria-hidden="true" />
                                    ) : (
                                        <FileText size={14} aria-hidden="true" />
                                    )}
                                    {t(
                                        meditation.has_audio
                                            ? "portal.meditations.withVoice"
                                            : "portal.meditations.toRead"
                                    )}
                                    <span aria-hidden="true">·</span>
                                    {runsFor(meditation)}
                                </span>
                                <span className="text-base font-semibold text-content-primary">
                                    {meditation.title}
                                </span>
                                {meditation.description && (
                                    <span className="line-clamp-2 text-sm text-content-secondary">
                                        {meditation.description}
                                    </span>
                                )}
                                <span className="text-xs text-content-tertiary">
                                    {t("portal.meditations.sharedOn", {
                                        date: givenOn(meditation.shared_at),
                                    })}
                                </span>
                            </Link>
                        </li>
                    ))}
                </ul>
            )}
        </>
    );
}
